import { useState, useMemo, useCallback } from "react";
import { View, Text, ActivityIndicator } from "react-native";
import { FlashList } from "@shopify/flash-list";
import { Stack, useLocalSearchParams } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { BackButton } from "@/shared/components/ui/back-button";
import { Heading } from "@/shared/components/ui/heading";
import { ItemSeparator } from "@/shared/components/ui/item-separator";
import { SearchInput } from "@/shared/components/ui/search-input";
import { UnderlineTabs } from "@/shared/components/ui/underline-tabs";
import { useUserEvents } from "@/features/events/api/event-participants";
import { EventCard } from "@/features/events/components/event-card";
import { useProfile } from "@/features/profile/api/profile";
import {
  EVENT_PARTICIPANT_CONFIRMED,
  EVENT_PARTICIPANT_INTERESTED,
} from "@/features/events/constants";
import type { Event } from "@lezz/shared";
import { toEvent } from "@/features/profile/lib/to-event";

const LIST_CONTENT_STYLE = { paddingHorizontal: 20, paddingTop: 20 };

const TABS = [
  { key: "confirmados", label: "Confirmados" },
  { key: "interesse", label: "Interesse" },
  { key: "passados", label: "Passados" },
] as const;

type Tab = (typeof TABS)[number]["key"];

export default function UserEventsListScreen() {
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const insets = useSafeAreaInsets();

  const [activeTab, setActiveTab] = useState<Tab>("confirmados");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortAsc] = useState(false);

  const { data: events, isLoading } = useUserEvents(userId);
  const { data: profile } = useProfile(userId);

  const filteredEvents = useMemo(() => {
    if (!events) return [];

    const now = new Date();
    const normalize = (s: string) =>
      s
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase();
    const normalizedQuery = searchQuery.trim() ? normalize(searchQuery) : null;

    return events
      .filter((e) => {
        if (activeTab === "confirmados") {
          return (
            e.participantType === EVENT_PARTICIPANT_CONFIRMED &&
            new Date(e.eventDateAndTime!) >= now
          );
        }
        if (activeTab === "interesse") {
          return (
            e.participantType === EVENT_PARTICIPANT_INTERESTED &&
            new Date(e.eventDateAndTime!) >= now
          );
        }
        return new Date(e.eventDateAndTime!) < now;
      })
      .filter((e) =>
        normalizedQuery
          ? normalize(e.eventTitle ?? "").includes(normalizedQuery)
          : true
      )
      .sort((a, b) => {
        const diff =
          new Date(a.eventDateAndTime!).getTime() -
          new Date(b.eventDateAndTime!).getTime();
        return sortAsc ? diff : -diff;
      })
      .map(toEvent);
  }, [events, activeTab, searchQuery, sortAsc]);

  const renderEventCard = useCallback(
    ({ item: event }: { item: Event }) => (
      <EventCard event={event} variant="compact" />
    ),
    []
  );

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 pb-4"
          style={{ paddingTop: insets.top || 24 }}
        >
          <BackButton />
          <Heading variant="section" accessibilityRole="header">
            {profile?.username ? `@${profile.username}` : ""}
          </Heading>
          <View className="h-10 w-10" />
        </View>

        <UnderlineTabs
          items={TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
        />

        <View className="px-5 pt-4 pb-2">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Pesquisar"
          />
        </View>

        {isLoading ? (
          <View className="flex-1 items-center justify-center">
            <ActivityIndicator
              size="small"
              className="text-brand"
              accessibilityLabel="Loading events"
            />
          </View>
        ) : (
          <FlashList
            data={filteredEvents}
            keyExtractor={(event) => event.eventId.toString()}
            renderItem={renderEventCard}
            contentInsetAdjustmentBehavior="automatic"
            ListEmptyComponent={
              <Text className="text-muted-foreground px-5 pt-4 text-sm">
                Sem eventos
              </Text>
            }
            ItemSeparatorComponent={ItemSeparator}
            contentContainerStyle={LIST_CONTENT_STYLE}
          />
        )}
      </ScreenContainer>
    </>
  );
}
